/* eslint-disable */
import { FuseNavigationItem } from '@fuse/components/navigation';

// Extended interface for navigation items with plugin and role support
interface PluginNavigationItem extends FuseNavigationItem {
    plugins?: string[]; // Optional array of required plugins (user must have ANY one plugin)
    roles?: string[]; // Optional array of required roles (user must have ANY one role)
    children?: PluginNavigationItem[];
}

// Base navigation items (with plugin requirements)
const baseNavigationItems: PluginNavigationItem[] = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        type: 'basic',
        icon: 'heroicons_outline:chart-pie', // Keep the dashboard icon
        link: '/dashboard',
    },
    {
        id: 'invoices',
        title: 'Sales Management',
        type: 'collapsable', // group
        icon: 'heroicons_outline:shopping-cart', // Updated icon for sales management
        roles: ['manager', 'owner', 'user'],
        children: [
            {
                id: 'invoices.add',
                title: 'Create Invoices',
                type: 'basic',
                icon: 'heroicons_outline:plus-circle', // Updated icon for creating invoices
                link: '/issue-invoice',
            },
            {
                id: 'invoices.recent',
                title: 'Issued Invoices',
                type: 'basic',
                icon: 'heroicons_outline:document-text', // Keep the issued invoices icon
                link: '/issued-invoices',
                roles: ['manager', 'owner'],
            },
            {
                id: 'invoices.failed',
                title: 'Failed Invoices',
                type: 'basic',
                icon: 'heroicons_outline:exclamation-circle', // Updated icon for failed invoices
                link: '/failed-invoices',
                roles: ['manager', 'owner'],
            },
            {
                id: 'invoices.customers',
                title: 'Customers',
                type: 'basic',
                icon: 'heroicons_outline:users', // Updated icon for customers
                link: '/customers',
            },
            {
                id: 'invoices.creditnotes',
                title: 'Creditnotes',
                type: 'basic',
                icon: 'heroicons_outline:receipt-refund', // Updated icon for credit notes
                link: '/credit-notes',
                roles: ['manager', 'owner'],
            },
        ],
    },
    {
        id: 'inventory',
        title: 'Stock Management',
        type: 'collapsable', // group
        icon: 'heroicons_outline:cube', // Updated icon for stock management
        roles: ['manager', 'owner', 'stockadmin'],
        children: [
            {
                id: 'inventory.stock',
                title: 'Items List',
                type: 'basic',
                icon: 'heroicons_outline:list-bullet', // Updated icon for items list
                link: '/items-list',
            },
            {
                id: 'inventory.purchases',
                title: 'Purchases',
                type: 'basic',
                icon: 'heroicons_outline:archive-box-arrow-down', // Updated icon for purchases
                link: '/purchases',
            },
            {
                id: 'inventory.suppliers',
                title: 'Suppliers',
                type: 'basic',
                icon: 'heroicons_outline:building-office', // Icon representing business/address
                link: '/suppliers',
            },
            {
                id: 'inventory.stock.transfer',
                title: 'Stock Transfer',
                type: 'basic',
                link: '/stock-transfers',
                icon: 'heroicons_outline:truck', // Updated icon for purchases
            },
            {
                id: 'inventory.stock.adjustment',
                title: 'Stock Adjustments',
                type: 'basic',
                icon: 'heroicons_outline:adjustments-horizontal', // Icon for adjustments
                link: '/adjustments',
            },
            {
                id: 'inventory.manufacture',
                title: 'Manufacturing',
                type: 'basic',
                icon: 'heroicons_outline:wrench-screwdriver', // Icon for manufacturing
                link: '/manufacture',
            },
        ],
    },
    {
        id: 'adv_inventory',
        title: 'Advanced Inventory',
        type: 'collapsable', // group
        icon: 'heroicons_outline:squares-plus',
        plugins: ['adv_inventory_mgmt'],
        roles: ['owner', 'stockadmin', 'manager'], // Available for admin or inventory manager roles
        children: [
            {
                id: 'adv_inventory.stock',
                title: 'Items List',
                type: 'basic',
                icon: 'heroicons_outline:clipboard-document-list',
                link: '/advanced-inventory',
            },
            {
                id: 'adv_inventory.purchases',
                title: 'Purchases',
                type: 'collapsable',
                icon: 'heroicons_outline:credit-card',
                children: [
                    {
                        id: 'adv_inventory.purchases.create',
                        title: 'Add',
                        type: 'basic',
                        icon: 'heroicons_outline:plus',
                        link: '/advanced-inventory-purchase',
                    },
                    {
                        id: 'adv_inventory.purchases',
                        title: 'List',
                        type: 'basic',
                        icon: 'heroicons_outline:table-cells',
                        link: '/advanced-inventory-purchases',
                    },
                ],
            },
            {
                id: 'adv_inventory.adjustments',
                title: 'Adjustments',
                type: 'collapsable',
                icon: 'heroicons_outline:adjustments-horizontal',
                children: [
                    {
                        id: 'adv_inventory.adjustments.create',
                        title: 'Add',
                        type: 'basic',
                        icon: 'heroicons_outline:plus',
                        link: '/advanced-inventory-adjustment',
                    },
                    {
                        id: 'adv_inventory.adjustments',
                        title: 'List',
                        type: 'basic',
                        icon: 'heroicons_outline:table-cells',
                        link: '/advanced-inventory-adjustments',
                    },
                ],
            },
            {
                id: 'adv_inventory.stock.transfer',
                title: 'Stock Transfer',
                type: 'collapsable',
                icon: 'heroicons_outline:arrow-path-rounded-square',
                children: [
                    {
                        id: 'adv_inventory.stock.transfer.create',
                        title: 'Add',
                        type: 'basic',
                        icon: 'heroicons_outline:plus',
                        link: '/advanced-inventory-transfer',
                    },
                    {
                        id: 'adv_inventory.stock.transfer.list',
                        title: 'List',
                        type: 'basic',
                        icon: 'heroicons_outline:table-cells',
                        link: '/advanced-inventory-transfers',
                    },
                ],
            },
        ],
    },
    {
        id: 'sales_distribution',
        title: 'Sales Distribution',
        type: 'collapsable', // group
        icon: 'heroicons_outline:truck',
        plugins: ['dstrbtr_sales_sht'],
        roles: ['owner', 'manager'],
        children: [
            {
                id: 'sales_distribution.sales.routes',
                title: 'Sales Routes',
                type: 'basic',
                icon: 'heroicons_outline:map',
                link: '/sales-routes',
            },
            {
                id: 'sales_distribution.sales.assignments',
                title: 'Sales Assignments',
                type: 'basic',
                icon: 'heroicons_outline:user-plus',
                link: '/sales-assignments',
            },
        ],
    },
    {
        id: 'reports',
        title: 'Reports',
        type: 'collapsable',
        icon: 'heroicons_outline:chart-bar', // General reports icon
        children: [
            {
                id: 'reports.buddy',
                title: 'Buddy Reports',
                type: 'collapsable',
                icon: 'heroicons_outline:presentation-chart-bar', // Analytics-focused reports
                roles: ['owner', 'stockadmin', 'manager', 'user'],
                children: [
                    {
                        id: 'reports.buddy.invoices',
                        title: 'Invoices',
                        type: 'basic',
                        icon: 'heroicons_outline:document-text',
                        link: '/buddy-sales-report',
                    },
                    {
                        id: 'reports.buddy.item-wise',
                        title: 'Items Sold',
                        type: 'basic',
                        icon: 'heroicons_outline:shopping-bag',
                        link: '/buddy-items-sales-report',
                    },
                    {
                        id: 'reports.buddy.customer-sales',
                        title: 'Customer Sales',
                        type: 'basic',
                        icon: 'heroicons_outline:users',
                        link: '/buddy-customer-sales-report',
                    },
                    {
                        id: 'reports.buddy.discounts',
                        title: 'Discounts',
                        type: 'collapsable',
                        icon: 'heroicons_outline:tag',
                        plugins: ['prce_lvls'],
                        roles: ['owner', 'manager'],
                        children: [
                            {
                                id: 'reports.buddy.discounts.usage-stats',
                                title: 'Usage Stats',
                                type: 'basic',
                                icon: 'heroicons_outline:list-bullet',
                                link: '/discount-reports/usage-stats',
                            },
                            {
                                id: 'reports.buddy.discounts.top-products',
                                title: 'Product Stats',
                                type: 'basic',
                                icon: 'heroicons_outline:list-bullet',
                                link: '/discount-reports/top-products',
                            },
                            {
                                id: 'reports.buddy.discounts.customer-insights',
                                title: 'Customers',
                                type: 'basic',
                                icon: 'heroicons_outline:list-bullet',
                                link: '/discount-reports/customer-insights',
                            },
                        ],
                    },
                ],
            },
            {
                id: 'reports.efris',
                title: 'EFRIS Reports',
                type: 'collapsable',
                icon: 'heroicons_outline:document-check', // Compliance/tax-related reports
                roles: ['owner', 'manager'],
                children: [
                    {
                        id: 'reports.efris.invoices',
                        title: 'Invoices',
                        type: 'basic',
                        icon: 'heroicons_outline:document-text',
                        link: '/efris-sales-report',
                    },
                ],
            },
        ],
    },
    {
        id: 'config',
        title: 'Configuration',
        type: 'collapsable', // group
        icon: 'heroicons_outline:cog', // Updated icon for configuration
        roles: ['owner', 'manager'],
        children: [
            {
                id: 'config.users',
                title: 'User Management',
                type: 'basic',
                icon: 'heroicons_outline:users',
                link: '/users',
            },
            {
                id: 'config.sales',
                title: 'Sales Management',
                type: 'collapsable',
                icon: 'heroicons_outline:chart-bar',
                plugins: ['prce_lvls', 'adv_sales_stkst'],
                children: [
                    {
                        id: 'config.sales.prce_lvls',
                        title: 'Discounts',
                        type: 'basic',
                        icon: 'heroicons_outline:tag',
                        link: '/discounts',
                        plugins: ['prce_lvls'],
                    },
                    {
                        id: 'config.sales.adv_sales_stkst',
                        title: 'Stockists',
                        type: 'basic',
                        icon: 'heroicons_outline:users',
                        link: '/stockists',
                        plugins: ['adv_sales_stkst'],
                    },
                ],
            },
            {
                id: 'config.system',
                title: 'System Settings',
                type: 'basic',
                icon: 'heroicons_outline:cog-6-tooth',
                link: '/system-settings',
            },
        ],
    },
    {
        id: 'clients',
        title: 'Clients Management',
        type: 'basic',
        icon: 'heroicons_outline:building-office-2',
        link: '/clients',
        roles: ['admin'],
    },
    {
        id: 'pricing',
        title: 'Pricing Management',
        type: 'basic',
        icon: 'heroicons_outline:currency-dollar',
        link: '/pricing',
        roles: ['admin'],
    },
];

// Helper function to filter navigation items based on user plugins and roles
export const getFilteredNavigation = (
    authService?: any
): FuseNavigationItem[] => {
    if (!authService) {
        // If no auth service provided, return all items (fallback)
        return baseNavigationItems as FuseNavigationItem[];
    }

    // Evaluate a single constraint list (ANY-of) against the user
    const passesAny = (list?: string[], checker?: (v: string) => boolean) => {
        if (!list || list.length === 0) return true; // no requirement
        if (!checker) return true; // no checker provided
        return list.some((v) => checker(v));
    };

    // Recursive filter with inherited (AND) gate for roles/plugins
    const filterWithInheritance = (
        items: PluginNavigationItem[],
        rolePassSoFar: boolean,
        pluginPassSoFar: boolean
    ): FuseNavigationItem[] => {
        return items
            .map((item) => {
                // Self checks at this level (ANY-of list)
                const selfRolePass = passesAny(
                    item.roles,
                    authService.userHasRole?.bind(authService)
                );
                const selfPluginPass = passesAny(
                    item.plugins,
                    authService.userHasPlugin?.bind(authService)
                );

                if (
                    item.id === 'invoices.add' &&
                    authService.userHasPlugin?.('manager_io')
                ) {
                    return null;
                }

                // Cumulative (AND across ancestor levels)
                const cumulativeRolePass = rolePassSoFar && selfRolePass;
                const cumulativePluginPass = pluginPassSoFar && selfPluginPass;

                // Recurse into children with cumulative pass gates
                let filteredChildren: FuseNavigationItem[] | undefined;
                if (item.children) {
                    filteredChildren = filterWithInheritance(
                        item.children,
                        cumulativeRolePass,
                        cumulativePluginPass
                    );
                }

                // If there are accessible children, keep parent as a container
                if (filteredChildren && filteredChildren.length > 0) {
                    const { plugins, roles, ...navigationItem } = item;
                    return {
                        ...navigationItem,
                        children: filteredChildren,
                    } as FuseNavigationItem;
                }

                // Leaf items require cumulative pass at this point
                if (
                    !item.children &&
                    cumulativeRolePass &&
                    cumulativePluginPass
                ) {
                    const { plugins, roles, ...navigationItem } = item;
                    return navigationItem as FuseNavigationItem;
                }

                // Otherwise, drop the item
                return null;
            })
            .filter((i) => i !== null);
    };

    return filterWithInheritance(baseNavigationItems, true, true);
};

// Default navigation (shows all items - for backward compatibility)
export const defaultNavigation: FuseNavigationItem[] =
    baseNavigationItems as FuseNavigationItem[];

export const compactNavigation: FuseNavigationItem[] = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        type: 'basic',
        icon: 'heroicons_outline:chart-pie',
        link: '/dashboard',
    },
];

export const futuristicNavigation: FuseNavigationItem[] = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        type: 'basic',
        icon: 'heroicons_outline:chart-pie',
        link: '/dashboard',
    },
];

export const horizontalNavigation: FuseNavigationItem[] = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        type: 'basic',
        icon: 'heroicons_outline:chart-pie',
        link: '/dashboard',
    },
];
